import React, { useState, useEffect, useRef } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal, IonPage } from "@ionic/react";
import { Helmet } from "react-helmet";

const allEmojis = [
    "🦁", "🐶", "🐱", "🐰", "🐭", "🐸", "🦉", "🦊", "🐯", "🐻", "🐷", "🐵", "🐢", "🐦", "🐙", "🐧",
    "🚗", "🚀", "🚲", "✈️", "🚢", "🚜", "🏠", "🏡", "🏫", "🏥", "🎈", "🎸", "🎤", "🎭", "🎮", "⚽",
    "🏀", "🏈", "🎾", "🥎", "🎯", "🛴", "🛵", "🚁", "🚂", "🚆", "📕", "📗", "📘", "📙", "📚", "📖"
];

const positions = [
    { number: 1, text: "1st", word: "First" },
    { number: 2, text: "2nd", word: "Second" },
    { number: 3, text: "3rd", word: "Third" },
    { number: 4, text: "4th", word: "Fourth" },
    { number: 5, text: "5th", word: "Fifth" },
    { number: 6, text: "6th", word: "Sixth" },
    { number: 7, text: "7th", word: "Seventh" },
    { number: 8, text: "8th", word: "Eighth" },
    { number: 9, text: "9th", word: "Nineth" },
    { number: 10, text: "10th", word: "Tenth" },
];

const IdentifyPosition: React.FC = () => {
    const [emojiRow, setEmojiRow] = useState<string[]>([]);
    const [question, setQuestion] = useState<string>("");
    const [correctAnswer, setCorrectAnswer] = useState<string>("");
    const [options, setOptions] = useState<string[]>([]);
    const [message, setMessage] = useState<string>("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    useEffect(() => {
        generateNewRound();
        document.title = "Ordinal Numbers Worksheet for LKG | 1st to 10th Learning Activity | Mama’s Guidance";
    }, []);

    const generateNewRound = () => {
        // Generate a random set of 8 emojis
        const newEmojiSet = [...allEmojis].sort(() => 0.5 - Math.random()).slice(0, 10);

        // Choose a random position
        const posIndex = Math.floor(Math.random() * positions.length);
        const posData = positions[posIndex];

        // Randomly decide whether to use "3rd" or "Third"
        const positionText = Math.random() < 0.5 ? posData.text : posData.word;

        // Get the correct answer
        const correctAnimal = newEmojiSet[posIndex];

        // Generate 2 random incorrect options
        let randomOptions = [...newEmojiSet].filter((emoji) => emoji !== correctAnimal);
        randomOptions = randomOptions.sort(() => 0.5 - Math.random()).slice(0, 2);

        // Mix correct answer with incorrect ones
        const answerChoices = [...randomOptions, correctAnimal].sort(() => 0.5 - Math.random());

        setEmojiRow(newEmojiSet);
        setQuestion(`Which object is ${positionText} in the row?`);
        setCorrectAnswer(correctAnimal);
        setOptions(answerChoices);
        setMessage("");
    };

    const checkAnswer = (selected: string) => {
        if (selected === correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                generateNewRound()
                setShowImage(false);
                setMessage("")
            }, 5000)
        } else {
            setShowImage(false);
            setMessage("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Teach LKG kids ordinal numbers with fun and easy-to-understand activities on Mama’s Guidance—helping children learn positions like first, second, and third through colorful, interactive exercises.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord ordinalNumber matchImage match-container game-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Ordinal Numbers</h1>

                                    <p>
                                        Understanding <strong>ordinal numbers</strong> is essential for children to describe position, order, and ranking. This LKG worksheet introduces <strong>1st to 10th</strong> using playful illustrations, real-life examples, and colorful activities that make learning both easy and enjoyable.
                                    </p>

                                    <h2>🔢 What Are Ordinal Numbers?</h2>
                                    <p>
                                        Ordinal numbers tell us the <strong>position or order</strong> of something in a list. For example:
                                        <ul>
                                            <li>1st – First</li>
                                            <li>2nd – Second</li>
                                            <li>3rd – Third</li>
                                            <li>4th – Fourth</li>
                                            <li>…up to 10th – Tenth</li>
                                        </ul>
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Recognize and understand ordinal numbers from 1st to 10th</li>
                                        <li>Use ordinal terms to describe position in a line or row</li>
                                        <li>Match numbers with positions in real-world scenes</li>
                                        <li>Improve vocabulary with words <strong>like first, second, third, etc.</strong></li>
                                    </ul>

                                    <h2>📋 What’s Inside the Worksheet?</h2>
                                    <ul>
                                        <li>Color the <strong>first</strong> object in the row</li>
                                        <li>Circle the <strong>third</strong> balloon or <strong>fifth</strong> duck</li>
                                        <li>Match ordinal numbers (<strong>1st to 10th</strong>) to labeled objects</li>
                                        <li>Draw lines to connect objects with their positions</li>
                                    </ul>

                                    <h2>👩‍🏫 Teaching Tips for Parents & Educators</h2>
                                    <ul>
                                        <li>Line up toys and ask your child: <strong>“Which is the second one?”</strong></li>
                                        <li>Use daily tasks: <strong>“Brush your teeth first, then eat.”</strong></li>
                                        <li>Practice with real-life situations: school bus queue, birthday line-up, or steps in a game</li>
                                        <li>Sing ordinal number rhymes for memory building</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/lkg/mathematics/missing-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Missing Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Before / After / Between Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/lkg/mathematics/skip-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Skip Number by 2 or 5</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🌟 Mama’s Guidance Tip</h2>
                                    <p>
                                        During playtime or while reading books, highlight positions: “The <strong>first</strong> animal is a lion, the <strong>second</strong> is a tiger...” Everyday conversations help build strong understanding!
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Ordinal Numbers</h1>

                                <p>
                                    Understanding <strong>ordinal numbers</strong> is essential for children to describe position, order, and ranking. This LKG worksheet introduces <strong>1st to 10th</strong> using playful illustrations, real-life examples, and colorful activities that make learning both easy and enjoyable.
                                </p>

                                <h2>🔢 What Are Ordinal Numbers?</h2>
                                <p>
                                    Ordinal numbers tell us the <strong>position or order</strong> of something in a list. For example:
                                    <ul>
                                        <li>1st – First</li>
                                        <li>2nd – Second</li>
                                        <li>3rd – Third</li>
                                        <li>4th – Fourth</li>
                                        <li>…up to 10th – Tenth</li>
                                    </ul>
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize and understand ordinal numbers from 1st to 10th</li>
                                    <li>Use ordinal terms to describe position in a line or row</li>
                                    <li>Match numbers with positions in real-world scenes</li>
                                    <li>Improve vocabulary with words <strong>like first, second, third, etc.</strong></li>
                                </ul>

                                <h2>📋 What’s Inside the Worksheet?</h2>
                                <ul>
                                    <li>Color the <strong>first</strong> object in the row</li>
                                    <li>Circle the <strong>third</strong> balloon or <strong>fifth</strong> duck</li>
                                    <li>Match ordinal numbers (<strong>1st to 10th</strong>) to labeled objects</li>
                                    <li>Draw lines to connect objects with their positions</li>
                                </ul>

                                <h2>👩‍🏫 Teaching Tips for Parents & Educators</h2>
                                <ul>
                                    <li>Line up toys and ask your child: <strong>“Which is the second one?”</strong></li>
                                    <li>Use daily tasks: <strong>“Brush your teeth first, then eat.”</strong></li>
                                    <li>Practice with real-life situations: school bus queue, birthday line-up, or steps in a game</li>
                                    <li>Sing ordinal number rhymes for memory building</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/mathematics/missing-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Missing Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/before-after-between-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Before / After / Between Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/mathematics/skip-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Skip Number by 2 or 5</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    During playtime or while reading books, highlight positions: “The <strong>first</strong> animal is a lion, the <strong>second</strong> is a tiger...” Everyday conversations help build strong understanding!
                                </p>
                            </article>
                            <div className="optionImageDiv">
                                {emojiRow.map((emoji, index) => (
                                    <span key={index} style={{ margin: "10px" }} onClick={() => checkAnswer(emoji)}>
                                        {emoji}
                                    </span>
                                ))}
                            </div>
                            <h3>{question}</h3>
                            <p style={{ fontSize: "20px", marginTop: "20px" }}>{message}</p>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default IdentifyPosition;
